import { addDays, type LocalDate } from '../dates';
import { singletonId } from '../ids';
import type { DateRange } from '../ledger/counting';
import {
  adjustPayday,
  NO_HOLIDAYS,
  occurrencesInRange,
  resolveTiedSchedules,
  type IncomeKind,
  type IncomeSourceLike,
} from './schedule';

export type PaydayStatus = 'expected' | 'received' | 'skipped';

/** A stored payday row (written only when the user confirms or skips). */
export interface PaydayRowLike {
  id: string;
  sourceId: string;
  /** The occurrence's nominal date: the key that ties a row to its scheduled payday. */
  scheduledDate: LocalDate;
  /** The day it actually came (received). */
  actualDate: LocalDate | null;
  defaultAmountMinor: number;
  amountMinor: number | null;
  status: PaydayStatus;
  ledgerEntryId: string | null;
  deletedAt: string | null;
}

/** A payday as the app shows it: the schedule with the user's confirmations and skips applied. */
export interface EffectivePayday {
  id: string;
  sourceId: string;
  sourceName: string;
  sourceKind: IncomeKind;
  nominalDate: LocalDate;
  /** Expected day (weekend rule applied), or the day it actually came once received. */
  date: LocalDate;
  /** The day it was scheduled to come (weekend rule applied), even once received on another day. */
  expectedDate: LocalDate;
  defaultAmountMinor: number;
  /** The received amount; null until received. */
  amountMinor: number | null;
  status: PaydayStatus;
  ledgerEntryId: string | null;
  /** True when a payday row exists for it. */
  stored: boolean;
  /**
   * v0.5: which payday of a twice-a-month salary it is, so cash flow can say "Salary (mid-month)"
   * and "Salary (end of month)"; null for every other schedule.
   */
  cutoff?: 'mid-month' | 'end of month' | null;
}

/**
 * Deterministic id, so two offline devices confirming the same payday converge on one row
 * (conventions §3): singletonId(userId, 'paydays.<sourceId>.<nominalDate>').
 */
export function paydayId(userId: string, sourceId: string, nominalDate: LocalDate): string {
  return singletonId(userId, `paydays.${sourceId}.${nominalDate}`);
}

/** A received or edited payday can land up to this many days away from its scheduled date. */
const MOVE_MARGIN_DAYS = 62;

const byDate = (a: EffectivePayday, b: EffectivePayday) =>
  a.date < b.date
    ? -1
    : a.date > b.date
      ? 1
      : a.sourceName < b.sourceName
        ? -1
        : a.sourceName > b.sourceName
          ? 1
          : 0;

/**
 * Every payday whose effective date is in `range`, ascending. Live sources only, plus received
 * rows whose source or schedule no longer produces them (their money is real and stays counted).
 * An unconfirmed payday stays 'expected' however old it is (Ivan, 2026-11-02).
 */
export function paydaysInRange(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  range: DateRange,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): EffectivePayday[] {
  return paydaysAround(userId, sources, rows, range, holidays)
    .filter((p) => p.date >= range.start && p.date <= range.end)
    .sort(byDate);
}

/**
 * Every payday whose scheduled day (expectedDate) is in `range`, wherever it actually came.
 * Cutoff views use this: a salary paid early (Dec 31's on Dec 23) still belongs to its own cutoff
 * (answered default, OPEN-QUESTIONS v0.2; Ivan can change it). Month views use paydaysInRange.
 */
export function paydaysScheduledIn(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  range: DateRange,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): EffectivePayday[] {
  return paydaysAround(userId, sources, rows, range, holidays)
    .filter((p) => p.expectedDate >= range.start && p.expectedDate <= range.end)
    .sort(byDate);
}

/** Paydays around `range` (wide enough for any received or edited one), unfiltered and unsorted. */
function paydaysAround(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  range: DateRange,
  holidays: ReadonlySet<LocalDate>,
): EffectivePayday[] {
  const live = rows.filter((r) => r.deletedAt === null);
  const rowByKey = new Map(live.map((r) => [`${r.sourceId}|${r.scheduledDate}`, r]));
  const sourceById = new Map(sources.map((s) => [s.id, s]));
  const wide: DateRange = {
    start: addDays(range.start, -MOVE_MARGIN_DAYS),
    end: addDays(range.end, MOVE_MARGIN_DAYS),
  };
  const out: EffectivePayday[] = [];
  const used = new Set<string>();

  // v0.5: bonuses tied to a salary payday follow that payday (resolveTiedSchedules).
  for (const s of resolveTiedSchedules(sources)) {
    for (const o of occurrencesInRange(s, wide, holidays)) {
      const key = `${s.id}|${o.nominalDate}`;
      const row = rowByKey.get(key);
      if (row) used.add(key);
      const received = row?.status === 'received';
      out.push({
        id: row?.id ?? paydayId(userId, s.id, o.nominalDate),
        sourceId: s.id,
        sourceName: s.name,
        sourceKind: s.kind,
        nominalDate: o.nominalDate,
        date: received ? (row.actualDate ?? o.date) : o.date,
        expectedDate: o.date,
        defaultAmountMinor:
          row && row.status !== 'expected' ? row.defaultAmountMinor : o.defaultAmountMinor,
        amountMinor: received ? row.amountMinor : null,
        status: row?.status ?? 'expected',
        ledgerEntryId: received ? row.ledgerEntryId : null,
        stored: !!row,
        ...(s.kind === 'salary' && s.schedule.kind === 'semi_monthly'
          ? { cutoff: o.slot === 0 ? ('mid-month' as const) : ('end of month' as const) }
          : {}),
      });
    }
  }

  for (const r of live) {
    if (used.has(`${r.sourceId}|${r.scheduledDate}`) || r.status !== 'received') continue;
    const s = sourceById.get(r.sourceId);
    out.push({
      id: r.id,
      sourceId: r.sourceId,
      sourceName: s?.name ?? '',
      sourceKind: s?.kind ?? 'salary',
      nominalDate: r.scheduledDate,
      date: r.actualDate ?? r.scheduledDate,
      expectedDate: s ? adjustPayday(r.scheduledDate, s.weekendRule, holidays) : r.scheduledDate,
      defaultAmountMinor: r.defaultAmountMinor,
      amountMinor: r.amountMinor,
      status: 'received',
      ledgerEntryId: r.ledgerEntryId,
      stored: true,
    });
  }

  return out;
}

/** How far back the app asks about unconfirmed paydays. */
export const DUE_LOOKBACK_DAYS = 45;

/** Paydays that have arrived (date <= today) but aren't confirmed or skipped yet, oldest first. */
export function duePaydays(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  today: LocalDate,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): EffectivePayday[] {
  return paydaysInRange(
    userId,
    sources,
    rows,
    { start: addDays(today, -DUE_LOOKBACK_DAYS), end: today },
    holidays,
  ).filter((p) => p.status === 'expected');
}

/** The next expected payday after today (today's own payday counts as due, not next). */
export function nextPayday(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  today: LocalDate,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): EffectivePayday | null {
  const upcoming = paydaysInRange(
    userId,
    sources,
    rows,
    { start: addDays(today, 1), end: addDays(today, 400) },
    holidays,
  );
  return upcoming.find((p) => p.status === 'expected') ?? null;
}

/** One payday by source and scheduled date (what a payday screen's URL names), or null. */
export function findPayday(
  userId: string,
  sources: readonly IncomeSourceLike[],
  rows: readonly PaydayRowLike[],
  sourceId: string,
  nominalDate: LocalDate,
  holidays: ReadonlySet<LocalDate> = NO_HOLIDAYS,
): EffectivePayday | null {
  const around: DateRange = {
    start: addDays(nominalDate, -MOVE_MARGIN_DAYS),
    end: addDays(nominalDate, MOVE_MARGIN_DAYS),
  };
  const mine = paydaysInRange(
    userId,
    resolveTiedSchedules(sources).filter((s) => s.id === sourceId),
    rows.filter((r) => r.sourceId === sourceId),
    around,
    holidays,
  );
  return mine.find((p) => p.nominalDate === nominalDate) ?? null;
}
